import { LitElement, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import type { PromptVariant } from '../../types/index.js';
import { createLoquixEvent } from '../../events/index.js';
import { KeyboardController } from '../../controllers/keyboard.controller.js';
import { ResizeController } from '../../controllers/resize.controller.js';
import styles from './loquix-prompt-input.styles.js';

/**
 * @tag loquix-prompt-input
 * @summary A textarea-based prompt input with keyboard shortcuts and auto-resize.
 *
 * @csspart container - Outer wrapper of the input.
 * @csspart input - The native `<textarea>` element.
 *
 * @fires loquix-change - When the input value changes.
 * @fires loquix-submit - When the user submits (Enter key or programmatic).
 *
 * @cssprop [--loquix-input-bg] - Background colour of the textarea.
 * @cssprop [--loquix-input-color] - Text colour.
 * @cssprop [--loquix-input-border-color] - Border colour.
 * @cssprop [--loquix-input-focus-border-color] - Border colour when focused.
 * @cssprop [--loquix-input-placeholder-color] - Placeholder text colour.
 * @cssprop [--loquix-input-font-family] - Font family.
 * @cssprop [--loquix-input-font-size] - Font size.
 * @cssprop [--loquix-input-padding] - Inner padding.
 * @cssprop [--loquix-input-border-radius] - Border radius.
 */
export class LoquixPromptInput extends LitElement {
  static styles = [styles];

  /** Visual variant of the input. */
  @property({ type: String, reflect: true })
  variant: PromptVariant = 'chat';

  /** Current value of the textarea. */
  @property({ type: String })
  value = '';

  /** Initial number of visible rows. */
  @property({ type: Number })
  rows = 1;

  /** Whether the textarea auto-grows with content. */
  @property({ type: Boolean, attribute: 'auto-resize' })
  autoResize = true;

  /** Whether pressing Enter submits (without Shift). */
  @property({ type: Boolean, attribute: 'submit-on-enter' })
  submitOnEnter = true;

  /** Placeholder text. */
  @property({ type: String })
  placeholder = 'Type a message...';

  /** Whether the input is disabled. */
  @property({ type: Boolean, reflect: true })
  disabled = false;

  @query('textarea')
  private _textarea!: HTMLTextAreaElement;

  private _keyboard = new KeyboardController(this);
  private _resize = new ResizeController(this);

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  protected firstUpdated(): void {
    const textarea = this._textarea;
    if (!textarea) return;

    // Attach keyboard controller to the textarea
    this._keyboard.attach(textarea);

    this._keyboard.addBinding({
      key: 'Enter',
      handler: (e) => {
        if (!this.submitOnEnter) return;
        e.preventDefault();
        this._handleSubmit();
      },
    });

    // Attach resize controller if auto-resize is enabled
    if (this.autoResize) {
      this._resize.attach(textarea);
    }
  }

  // ---------------------------------------------------------------------------
  // Event helpers
  // ---------------------------------------------------------------------------

  private _handleInput(e: Event): void {
    const textarea = e.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.dispatchEvent(
      createLoquixEvent('loquix-change', { value: this.value }),
    );
  }

  private _handleSubmit(): void {
    const content = this.value.trim();
    if (!content) return;
    this.dispatchEvent(
      createLoquixEvent('loquix-submit', { content }),
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  protected render() {
    return html`
      <div part="container" class="container">
        <textarea
          part="input"
          class="input"
          .value=${this.value}
          rows=${this.rows}
          placeholder=${this.placeholder}
          ?disabled=${this.disabled}
          @input=${this._handleInput}
        ></textarea>
      </div>
    `;
  }
}
